// @mui material components
import { Box, Icon } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from "react";

import { wrapper } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";

import GroupMapView from "pages/components/Groups/GroupMap/GroupMapView";
import NewGroupView from "pages/components/Groups/NewGroup/NewGroupView";
import { Link } from "react-router-dom";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function DeviceGroupsView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    const [addNewGroup, setAddNewGroup] = useState(false);
    const [editGroupId, setEditGroupId] = useState(null);
    const [splitMap, setSplitMap] = useState(false); 

    const localMapContainer = useRef(null);
    const localMap = useRef(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg, map, user]);

    useEffect(() => {
        if (localMap.current || !localMapContainer.current) return;

        const orgLocation = user?.organisations?.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 };

        localMap.current = new mapboxgl.Map({
            container: localMapContainer.current,
            style: 'mapbox://styles/mapbox/dark-v11',
            center: Object.values(orgLocation).reverse(),
            zoom: 10,
            attributionControl: false
        });

        localMap.current.on('load', () => setIsMapLoaded(true));

        return () => {
            if (localMap.current) {
                localMap.current.remove();
                localMap.current = null;
                setIsMapLoaded(false);
            }
        };
    }, [selectedOrg, user]);

    useEffect(() => {
        if (!localMapContainer.current || !localMap.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (localMap.current) {
                localMap.current.resize();
            }
        });

        resizeObserver.observe(localMapContainer.current);

        return () => resizeObserver.disconnect();
    }, [isMapLoaded]);

    const canGroupsAdd = userPermissions.indexOf('devices-groups:add') >= 0;

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox my={1} display="flex" sx={{ borderRadius: '16px', height: `calc(100% - 16px)` }}>
                <SoftBox mx={1} display="flex" flexDirection="column" borderRadius="16px" color={"white"} width="100%" height="100%"
                    sx={({ functions: { rgba } }) => ({ backgroundColor: rgba("#eeeeee", 1), overflow: "hidden", minWidth: 0, width: '100%' })}
                >
                    <SoftBox py={1} px={{ xxs: 1, md: 2 }} sx={({ functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba("#dcdcdc", 1), borderBottom: "1px solid #8e98a5" })}>
                        <SoftBox display="flex" flexDirection={{ xxs: "column", md: "row" }} justifyContent="space-between" gap={1.5}>
                            {(!addNewGroup && editGroupId === null) ? (
                                <>
                                    <SoftBox display={{ xxs: 'none', md: 'block' }} flex={{ xxs: "0", md: "0 0 auto" }} minWidth={{ md: "140px" }}>
                                        <SoftButton variant="contained" size="small" color="light" fullWidth
                                            sx={({ breakpoints }) => ({
                                                whiteSpace: "nowrap", height: '40px !important',
                                                [breakpoints.down("sm")]: { minHeight: '1.75rem !important', height: '1.75rem !important' }
                                            })}
                                            component={Link} to="/devices"
                                        >
                                            <Icon sx={{ mr: 0.5, fontWeight: "bold" }}>arrow_back</Icon>
                                            {deviceLayout === "mobile" ? "Return" : "Return to Devices"}
                                        </SoftButton>
                                    </SoftBox>
                                    {canGroupsAdd && (
                                        <SoftBox display={{ xxs: 'none', md: 'block' }} flex={{ xxs: "0", md: "0 0 auto" }} minWidth={{ md: "140px" }}>
                                            <SoftButton variant="gradient" size="small" color="dark" fullWidth onClick={() => setAddNewGroup(true)}
                                                sx={({ breakpoints }) => ({
                                                    whiteSpace: "nowrap", height: '40px !important',
                                                    [breakpoints.down("sm")]: { minHeight: '1.75rem !important', height: '1.75rem !important' }
                                                })}
                                            >
                                                <Icon sx={{ mr: 0.5, fontWeight: "bold" }}>add</Icon>
                                                {deviceLayout === "mobile" ? "New" : "New Group"}
                                            </SoftButton>
                                        </SoftBox>
                                    )}
                                </>
                            ) : (addNewGroup || editGroupId !== null) && (
                                <SoftBox display={{ xxs: 'none', md: 'block' }} flex={{ xxs: "0", md: "0 0 auto" }} minWidth={{ md: "140px" }}>
                                    <SoftButton variant="contained" size="small" color="light" fullWidth
                                        onClick={() => {
                                            setAddNewGroup(false);
                                            setEditGroupId(null);
                                            setSplitMap(false);
                                        }}
                                        sx={({ breakpoints }) => ({
                                            whiteSpace: "nowrap", height: '40px !important',
                                            [breakpoints.down("sm")]: { minHeight: '1.75rem !important', height: '1.75rem !important' }
                                        })}
                                    >
                                        back
                                    </SoftButton>
                                </SoftBox>
                            )}
                        </SoftBox>
                    </SoftBox>

                    {/* CSS GRID LAYOUT - Adjusted to 65% / 35% */}
                    <SoftBox sx={{ 
                        borderRadius: '0 0 16px 16px', 
                        display: 'grid', 
                        gridTemplateColumns: { xxs: '1fr', md: 'auto 1fr' },
                        gridTemplateRows: splitMap ? { xxs: 'auto 65vh 35vh', md: '65% 35%' } : { xxs: 'auto 50vh', md: '100%' },
                        flex: 1, 
                        overflow: 'hidden' 
                    }}>
                        {(!addNewGroup && editGroupId === null) ? (
                            <GroupMapView {...{ user, localMap, isMapLoaded, setEditGroupId, setSplitMap }} />
                        ) : (
                            <NewGroupView {...{ user, localMap, isMapLoaded, setAddNewGroup, editGroupId, setEditGroupId, setSplitMap }} />
                        )}

                        <SoftBox sx={{ 
                            gridColumn: { md: 2 }, 
                            gridRow: splitMap ? { xxs: 2, md: 1 } : { xxs: 2, md: '1 / span 2' }, 
                            minWidth: 0, position: 'relative' 
                        }}>
                            <Box ref={localMapContainer} sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }} />
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    ), controller);
}

DeviceGroupsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DeviceGroupsView;